// Company cash statement: funds the customer forecast month by month and fills
// any gap below the minimum close balance from the cheapest source with room
// (the AR facility or a term lender).
//
// Customer rows (all from the Sources & Uses forecast):
//   Returns (customers)  = sum of `returns` (cash collected, fees included)
//   Fee (customers)      = sum of `facilityFeePayment` (facility fee cash, paid separately)
//   Credit deployed      = sum of `growthInvestments`
// The accrued `fee` field is not added: that yield is already inside `returns`.
//
// Rates are entered as annual IRR percents. Each month charges the monthly
// equivalent, (1 + IRR)^(1/12) - 1, on the balance at the start of the month:
// 24% a year is 1.809% a month. An AR draw is charged one month: 89,000 drawn in
// November at 24% a year costs 1,610, paid in December with the repayment.
// Inputs saved before this (rateBasis missing) held monthly rates; withLenders
// converts them to the same annual IRR, and the statement reads them as monthly.

import { shiftMonth } from './pipelineExposure';

export const RATE_BASIS_ANNUAL_IRR = 'annualIrr';

// monthlyRateFromAnnualIrr turns an annual IRR % into the monthly % that compounds to it.
export function monthlyRateFromAnnualIrr(annualPct) {
  const annual = Number(annualPct) / 100;
  if (!Number.isFinite(annual) || annual <= -1) return 0;
  return (Math.pow(1 + annual, 1 / 12) - 1) * 100;
}

export function annualIrrFromMonthlyRate(monthlyPct) {
  const monthly = Number(monthlyPct) / 100;
  if (!Number.isFinite(monthly) || monthly <= -1) return 0;
  return (Math.pow(1 + monthly, 12) - 1) * 100;
}

function round4(value) {
  return Math.round(value * 10000) / 10000;
}

export const EMPTY_COMPANY_CASH_INPUTS = {
  // Lender rates and the AR fee are annual IRR percents.
  rateBasis: RATE_BASIS_ANNUAL_IRR,
  openingCash: 0,
  minCloseBalance: 0,
  monthlyOpex: 0,
  capitalAmount: 0,
  capitalMonth: '',
  // AR facility: the percent of next month's customer cash that can be borrowed now.
  arCapPct: 0,
  arRate: 0,
  // Term lenders, numbered by position: [{ name, cap, rate, startMonth }].
  // A lender can be drawn from its start month on (from the first month when empty).
  lenders: [],
};

const EPS = 0.005;
const MONTH_PATTERN = /^\d{4}-\d{2}$/;

function num(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function round2(value) {
  return Math.round(value * 100) / 100;
}

// Inputs saved before lenders existed had one seed and one institutional facility.
function legacyLenders(input) {
  const lenders = [];
  if (num(input.seedCap) > 0) {
    lenders.push({ name: 'Seed notes', cap: input.seedCap, rate: input.seedRate, startMonth: '' });
  }
  if (num(input.institutionalCap) > 0) {
    lenders.push({ name: 'Institutional debt', cap: input.institutionalCap, rate: input.institutionalRate, startMonth: '' });
  }
  return lenders;
}

// normalizeLender keeps the rate as entered and adds monthlyRate, the % charged each month.
export function normalizeLender(raw, annual = true) {
  const startMonth = String(raw?.startMonth || '');
  const rate = num(raw?.rate);
  return {
    name: String(raw?.name || '').trim(),
    cap: Math.max(0, num(raw?.cap)),
    rate,
    monthlyRate: annual ? monthlyRateFromAnnualIrr(rate) : rate,
    startMonth: MONTH_PATTERN.test(startMonth) ? startMonth : '',
  };
}

export function normalizeCompanyCashInputs(raw) {
  const input = { ...EMPTY_COMPANY_CASH_INPUTS, ...(raw || {}) };
  const lenders = Array.isArray(raw?.lenders) ? raw.lenders : legacyLenders(input);
  // Without rateBasis the inputs predate annual IRR and hold monthly rates.
  const annual = raw?.rateBasis === RATE_BASIS_ANNUAL_IRR;
  return {
    rateBasis: annual ? RATE_BASIS_ANNUAL_IRR : 'monthly',
    openingCash: num(input.openingCash),
    minCloseBalance: num(input.minCloseBalance),
    monthlyOpex: Math.max(0, num(input.monthlyOpex)),
    capitalAmount: Math.max(0, num(input.capitalAmount)),
    capitalMonth: String(input.capitalMonth || ''),
    arCapPct: Math.min(100, Math.max(0, num(input.arCapPct))),
    arRate: num(input.arRate),
    arMonthlyRate: annual ? monthlyRateFromAnnualIrr(input.arRate) : num(input.arRate),
    lenders: lenders.map((lender) => normalizeLender(lender, annual)),
  };
}

// withLenders returns raw inputs with the lender list filled in, so the screen can
// edit inputs saved before lenders existed.
export function withLenders(raw) {
  const input = { ...EMPTY_COMPANY_CASH_INPUTS, ...(raw || {}) };
  let out = input;
  if (!Array.isArray(raw?.lenders)) {
    const {
      seedCap, seedRate, institutionalCap, institutionalRate, institutionalRateMode, institutionalSteps, ...rest
    } = input;
    out = { ...rest, lenders: legacyLenders(input) };
  }
  if (raw && raw.rateBasis !== RATE_BASIS_ANNUAL_IRR) {
    // Monthly rates saved before annual IRR: show the annual IRR they compound to.
    out = {
      ...out,
      rateBasis: RATE_BASIS_ANNUAL_IRR,
      arRate: round4(annualIrrFromMonthlyRate(out.arRate)),
      lenders: (out.lenders || []).map((lender) => ({ ...lender, rate: round4(annualIrrFromMonthlyRate(lender.rate)) })),
    };
  }
  return out;
}

export function lenderLabel(lender, index) {
  const name = String(lender?.name || '').trim();
  return name ? `Lender ${index + 1} - ${name}` : `Lender ${index + 1}`;
}

export function monthRange(startMonth, endMonth) {
  if (!/^\d{4}-\d{2}$/.test(String(startMonth || '')) || !/^\d{4}-\d{2}$/.test(String(endMonth || ''))) return [];
  const months = [];
  for (let month = startMonth; month <= endMonth && months.length < 600; month = shiftMonth(month, 1)) {
    months.push(month);
  }
  return months;
}

// customerCashSeries adds up the forecast for the given customers in each month.
export function customerCashSeries(customers, months) {
  const index = new Map(months.map((month, position) => [month, position]));
  const returns = months.map(() => 0);
  const fee = months.map(() => 0);
  const deployed = months.map(() => 0);
  (customers || []).forEach((customer) => {
    (customer?.months || []).forEach((row) => {
      const position = index.get(row?.month);
      if (position === undefined) return;
      returns[position] += num(row.returns);
      fee[position] += num(row.facilityFeePayment);
      deployed[position] += num(row.growthInvestments);
    });
  });
  return { months, returns, fee, deployed };
}

// companyCashStatement runs the month-by-month statement.
// Each month: open + customer cash + capital - credit deployed - opex
//   - last month's AR draw and its one month of interest
//   - interest on each lender's balance at the start of the month.
// A gap below the minimum close is drawn from the cheapest source with room: the
// AR facility or a lender whose start month has come (ties: AR, then the lower
// lender number). Cash above the minimum close repays lenders, most expensive
// first (ties: the higher lender number). AR is always repaid in full the month
// after it is drawn. The most it can lend in a month is the AR cap percent of next
// month's customer cash (returns plus fees): with 1,000,000 coming in February and
// an 80% cap, January can borrow up to 800,000.
export function companyCashStatement({ months, returns, fee, deployed }, rawInputs) {
  const inputs = normalizeCompanyCashInputs(rawInputs);
  const { lenders } = inputs;
  let cash = inputs.openingCash;
  const balances = lenders.map(() => 0);
  let arOutstanding = 0;
  const rows = [];

  months.forEach((month, index) => {
    const open = cash;
    const capital = month === inputs.capitalMonth ? inputs.capitalAmount : 0;
    const customerReturns = num(returns[index]);
    const customerFee = num(fee[index]);
    const creditDeployed = num(deployed[index]);
    const arRepaid = arOutstanding;
    const arInterest = (arOutstanding * inputs.arMonthlyRate) / 100;
    const interest = lenders.map((lender, position) => (balances[position] * lender.monthlyRate) / 100);
    const lenderInterest = interest.reduce((sum, value) => sum + value, 0);
    arOutstanding = 0;

    let balance = open + capital + customerReturns + customerFee
      - creditDeployed - inputs.monthlyOpex - arRepaid - arInterest - lenderInterest;

    const drawn = lenders.map(() => 0);
    const repaid = lenders.map(() => 0);
    let arDrawn = 0;

    // Draw cheapest first until the minimum close is reached or every source is full.
    const nextCustomerCash = index + 1 < months.length ? num(returns[index + 1]) + num(fee[index + 1]) : 0;
    const arLimit = Math.max(0, (nextCustomerCash * inputs.arCapPct) / 100);
    for (let guard = 0; guard < 200 && inputs.minCloseBalance - balance > EPS; guard += 1) {
      const options = [];
      if (arLimit - arDrawn > EPS) options.push({ source: -1, rate: inputs.arMonthlyRate, room: arLimit - arDrawn });
      lenders.forEach((lender, position) => {
        const available = !lender.startMonth || lender.startMonth <= month;
        const room = lender.cap - balances[position];
        if (available && room > EPS) options.push({ source: position, rate: lender.monthlyRate, room });
      });
      if (!options.length) break;
      options.sort((left, right) => left.rate - right.rate || left.source - right.source);
      const pick = options[0];
      const amount = Math.min(pick.room, inputs.minCloseBalance - balance);
      if (pick.source < 0) {
        arDrawn += amount;
      } else {
        drawn[pick.source] += amount;
        balances[pick.source] += amount;
      }
      balance += amount;
    }

    // Repay the most expensive lender with cash above the minimum close.
    // Nothing is repaid in a month that needed a draw.
    const drewThisMonth = arDrawn + drawn.reduce((sum, value) => sum + value, 0) > 0;
    for (let guard = 0; !drewThisMonth && guard < 200 && balance - inputs.minCloseBalance > EPS; guard += 1) {
      const options = lenders
        .map((lender, position) => ({ source: position, rate: lender.monthlyRate, amount: balances[position] }))
        .filter((option) => option.amount > EPS);
      if (!options.length) break;
      options.sort((left, right) => right.rate - left.rate || right.source - left.source);
      const pick = options[0];
      const amount = Math.min(pick.amount, balance - inputs.minCloseBalance);
      repaid[pick.source] += amount;
      balances[pick.source] -= amount;
      balance -= amount;
    }

    arOutstanding = arDrawn;
    cash = balance;

    const sum = (values) => values.reduce((total, value) => total + value, 0);
    const totalInflow = capital + sum(drawn) + arDrawn + customerReturns + customerFee;
    const totalOutflow = creditDeployed + sum(repaid) + lenderInterest + arRepaid + arInterest + inputs.monthlyOpex;
    rows.push({
      month,
      openBalance: round2(open),
      capital: round2(capital),
      arDrawn: round2(arDrawn),
      returns: round2(customerReturns),
      fee: round2(customerFee),
      totalInflow: round2(totalInflow),
      creditDeployed: round2(creditDeployed),
      arRepaid: round2(arRepaid),
      arInterest: round2(arInterest),
      opex: round2(inputs.monthlyOpex),
      totalOutflow: round2(totalOutflow),
      netCashFlow: round2(totalInflow - totalOutflow),
      closeBalance: round2(cash),
      arBalance: round2(arOutstanding),
      shortfall: round2(Math.max(0, inputs.minCloseBalance - cash)),
      lenders: lenders.map((_, position) => ({
        drawn: round2(drawn[position]),
        repaid: round2(repaid[position]),
        interest: round2(interest[position]),
        balance: round2(balances[position]),
      })),
    });
  });

  return rows;
}

// companyCashRows lists the statement lines, with one set of rows per lender.
// value(row) reads a line from a statement row; section lines have no value.
export function companyCashRows(rawLenders) {
  const lenders = (rawLenders || []).map(normalizeLender);
  const field = (key) => (row) => row[key];
  const lenderLines = (key, suffix) => lenders.map((lender, position) => ({
    label: `${lenderLabel(lender, position)} - ${suffix}`,
    value: (row) => row.lenders?.[position]?.[key],
  }));
  return [
    { label: 'Open Balance', value: field('openBalance'), kind: 'total' },
    { label: '① CASH INFLOW', kind: 'section' },
    { label: 'Capital Fund', value: field('capital') },
    ...lenderLines('drawn', 'drawn'),
    { label: 'AR Facility - drawn', value: field('arDrawn') },
    { label: 'Returns (customers)', value: field('returns') },
    { label: 'Fee (customers)', value: field('fee') },
    { label: 'Total Cash Inflow', value: field('totalInflow'), kind: 'total' },
    { label: '② CASH OUTFLOW', kind: 'section' },
    { label: 'Growth Investments - credit deployed', value: field('creditDeployed') },
    ...lenders.flatMap((lender, position) => [
      { label: `${lenderLabel(lender, position)} - repaid`, value: (row) => row.lenders?.[position]?.repaid },
      { label: `${lenderLabel(lender, position)} - interest`, value: (row) => row.lenders?.[position]?.interest },
    ]),
    { label: 'AR Facility - repaid', value: field('arRepaid') },
    { label: 'AR Facility - interest', value: field('arInterest') },
    { label: 'Operating expenses', value: field('opex') },
    { label: 'Total Cash Outflow', value: field('totalOutflow'), kind: 'total' },
    { label: 'Net Cash Flow', value: field('netCashFlow'), kind: 'total' },
    { label: 'Close Balance', value: field('closeBalance'), kind: 'total', key: 'closeBalance' },
    { label: 'Facility balances', kind: 'section' },
    ...lenderLines('balance', 'outstanding'),
    { label: 'AR Facility - outstanding', value: field('arBalance') },
    { label: 'Below minimum close', value: field('shortfall'), kind: 'warning' },
  ];
}
